import { EventEmitter, Injectable } from "@angular/core";
import { Linqset, LinqsetNode, NODE_SUPERTYPE, getInsightsWithoutCreate, isRoleSystem } from "../common/linqset-model";
import { Organisation, findTagForOrganisation } from "../common/organisation";
import { OrganisationService } from "../organisation/organisation.service";
import { AdjacencyTypes, GraphComponent, IGraph, INode } from "@yfiles/yfiles";

class FilterData {
    nodes:LinqsetNode[] = []

    tagKeys:string[] = []
    tagMap = {}

    filterSystems:boolean
    filterRoles:boolean

    clear() {
        this.nodes = []
        this.tagKeys = []
        this.tagMap = {}
    }

    addNode(node:LinqsetNode) {
        this.nodes.push(node)
    }

    removeNode(node:LinqsetNode) {
        this.nodes = this.nodes.filter(n => n._key !== node._key)
    }

    hasNode(node:LinqsetNode) {
        return this.nodes.some(n => n._key === node?._key)
    }

    includeNode(node:LinqsetNode) {
        if (node.superType === NODE_SUPERTYPE.ROLE && !this.filterRoles) return true          // all roles are potentially included
        if (node.superType === NODE_SUPERTYPE.SYSTEM && !this.filterSystems) return true          // all systems are potentially included
        return this.hasNode(node)
    }

    startFilter(organisation:Organisation) {
        this.tagMap = this.tagKeys.reduce((m, tk) => {
            const group = findTagForOrganisation(organisation, tk)
            if (group) {
                if (!m[group.group._key]) m[group.group._key] = []
                m[group.group._key].push(tk)
            }
            return m
        }, {})

        this.filterRoles = this.nodes.some(n => n.superType === NODE_SUPERTYPE.ROLE)
        this.filterSystems = this.nodes.some(n => n.superType === NODE_SUPERTYPE.SYSTEM)
    }

    // whether a node has one of the chosen tags in every tag group a tag was chosen from
    hasTagKeys(node:LinqsetNode) {
        if (!this.tagKeys || this.tagKeys.length===0) return true

        const nodeTagKeys = node?.properties?.tagKeys
        if (!nodeTagKeys) return false

        for (const k of Object.keys(this.tagMap)) {
            if (!nodeTagKeys[k] || this.tagMap[k].indexOf(nodeTagKeys[k]) < 0) return false
        }
        return true
    }
}

@Injectable({
    providedIn: 'root'
})
export class FilterService {
    isFiltered:boolean = false
    private filterData = new FilterData()
    private actionsOnly:boolean = false

    // action nodes already traced forward or back while applying a filter. Tracing stops at them, so a model with
    // a loop in it can't recurse forever, and one where paths split and rejoin isn't walked once per path
    private tracedForward = new Set<INode>()
    private tracedBack = new Set<INode>()

    filterEvent = new EventEmitter<string>();
    constructor(private organisationService:OrganisationService) {
    }

    setTagKeys(tags:string[]) {
        // clearing the tag list in the filter dialog gives null
        this.filterData.tagKeys = tags || []
    }

    getTagKeys() {
        return this.filterData.tagKeys
    }

    getNodes() {
        return this.filterData.nodes
    }

    hasNode(node:LinqsetNode) {
        return this.filterData.hasNode(node)
    }

    setNodes(nodes:LinqsetNode[]) {
        this.filterData.nodes = nodes || []
        this.actionsOnly = false
    }

    setINodes(nodes:INode[]) {
        this.filterData.nodes = (nodes || []).filter(n => {
            return n.tag?.node?.superType === NODE_SUPERTYPE.ACTION
        }).map(n => n.tag.node)
        this.actionsOnly = true
    }

    addNode(node:LinqsetNode) {
        this.filterData.addNode(node)
    }

    removeNode(node:LinqsetNode) {
        this.filterData.removeNode(node)
    }

    public clearFilter(graphComponent:GraphComponent) {
        this.filterData.clear()
        this.actionsOnly = false
        if (!this.isFiltered) return

        graphComponent.graph.edges.filter(e => e.tag && e.tag.edge).forEach(e => {
            e.tag.excluded = false
        })

        graphComponent.graph.nodes.filter(n => n.tag?.node).forEach(n => {
            n.tag.includeNode()
        })
        graphComponent.updateVisualAsync()
        this.isFiltered = false
        this.filterEvent.emit("clear")
    }

    private resetGraph(graph:IGraph) {
        graph.edges.filter(e => e.tag && e.tag.edge).forEach(e => {
            e.tag.excluded = true
        })

        graph.nodes.filter(n => n.tag?.node).forEach(n => {
            n.tag.excludeNode()
        })
    }

    private applyStyles(graphComponent:GraphComponent) {
        graphComponent.updateVisualAsync()
        this.isFiltered = true
    }

    public applyFilter(linqset:Linqset, graphComponent:GraphComponent) {
        const {graph} = graphComponent

        this.resetGraph(graph)
        this.tracedForward.clear()
        this.tracedBack.clear()

        this.filterData.startFilter(this.organisationService.organisation)

        if (this.filterData.tagKeys.length > 0 && this.filterData.nodes.length === 0) {
            graph.nodes.filter(n => n.tag?.node?.superType === NODE_SUPERTYPE.ACTION && this.filterData.hasTagKeys(n.tag.node)).forEach(n => {
                this.processActionNode(n, graph)
            })
        }else {
            this.filterData.nodes.forEach(ln => {
                const node = graph.nodes.find(n => n.tag?.node?._key === ln._key)
                // a chosen node this view doesn't show adds nothing (roles and systems are looked for below, as a view can show them more than once)
                if (!node && !isRoleSystem(ln.superType)) return

                if (ln.superType === NODE_SUPERTYPE.OUTCOMES && !this.actionsOnly) {
                    node.tag.includeNode()
                    graph.edgesAt(node, AdjacencyTypes.INCOMING).filter(e => e.sourceNode.tag?.node).forEach(e => {
                        if (e.tag) e.tag.excluded = false
                        e.sourceNode.tag.includeNode()
                        graph.edgesAt(e.sourceNode, AdjacencyTypes.INCOMING)
                            .filter(e => e.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.ACTION)
                            .forEach(ae => {
                                this.populateBackwardEdgesForActionNode(ae.sourceNode, graph)
                        })
                    })
                }else if (ln.superType === NODE_SUPERTYPE.INFORMATION && !this.actionsOnly) {
                    // only info nodes that are outputs nodes can be selected right now
                    node.tag.includeNode()
                    const insights = getInsightsWithoutCreate(ln, linqset)
                    if (insights) {
                        if (insights.isOutput || insights.isLastNode) {
                            graph.edgesAt(node, AdjacencyTypes.INCOMING)
                                .filter(e => e.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.ACTION)
                                .forEach(ae => {
                                    this.populateBackwardEdgesForActionNode(ae.sourceNode, graph)
                            })
                        }else if (insights.isInput) {
                            // is input node so add all downstream from here
                            graph.edgesAt(node, AdjacencyTypes.OUTGOING)
                                .filter(e => e.targetNode.tag?.node?.superType === NODE_SUPERTYPE.ACTION)
                                .forEach(ae => {
                                    this.populateForwardEdgesForActionNode(ae.targetNode, graph)
                            })

                        }
                    }
                }else if (ln.superType === NODE_SUPERTYPE.ACTION) {
                    if (this.actionsOnly) {
                        this.processActionNode(node, graph)
                    }else {
                        const insights = getInsightsWithoutCreate(ln, linqset)
                        if (insights) {
                            if (insights.isOutput || insights.isLastNode) {
                                // is output or last node so add all upstream from here
                                this.populateBackwardEdgesForActionNode(node, graph)
                            }else if (insights.isInput) {
                                // is input node so add all downstream from here
                                this.populateForwardEdgesForActionNode(node, graph)
                            }else {
                                // normal action node so add just this node and its associated nodes
                                this.processActionNode(node, graph)
                            }
                        }
                    }
                }else if (isRoleSystem(ln.superType) && !this.actionsOnly) {
                    // look for all attached actions and info nodes and add them and their associated nodes
                    const nodes = graph.nodes.filter(n => n.tag?.node?._key === ln._key)
                    nodes.forEach(node => {
                        node.tag.includeNode()

                        graph.edgesAt(node, AdjacencyTypes.OUTGOING).filter(e => e.targetNode.tag?.node).forEach(e => {
                            if (e.targetNode.tag.node.superType === NODE_SUPERTYPE.ACTION) {
                                e.tag.excluded = false
                                this.processActionNode(e.targetNode, graph)
                            }else if (isRoleSystem(e.targetNode.tag.node.superType)) {
                                e.tag.excluded = false
                                graph.edgesAt(e.targetNode, AdjacencyTypes.OUTGOING).filter(se => se.targetNode.tag?.node?.superType === NODE_SUPERTYPE.ACTION).forEach(se => {
                                    se.tag.excluded = false
                                    this.processActionNode(se.targetNode, graph)
                                })
                            }else if (e.targetNode.tag.node.superType === NODE_SUPERTYPE.INFORMATION) {
                                e.tag.excluded = false
                                e.targetNode.tag.includeNode()
                            }
                        })
                    })
                }
            })
        }
        this.applyStyles(graphComponent)
        this.filterEvent.emit("applied")
    }

    // includes an action with what it creates and who or what performs it, if it has the chosen tags
    private processActionNode(node:INode, graph:IGraph) {
        if (!node?.tag?.node || !this.filterData.hasTagKeys(node.tag.node)) return

        node.tag.includeNode()
        const edges = graph.edgesAt(node)
        edges.forEach(e => {
            if (e.sourceNode === node && e.targetNode.tag?.node?.superType === NODE_SUPERTYPE.INFORMATION) {
                // include the next info nodes and their systems
                e.tag.excluded = false
                e.targetNode.tag.includeNode()

                // include systems that provide the info and the edge to the next action if that action is included
                graph.edgesAt(e.targetNode).filter(ie => ie.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.SYSTEM || (ie.targetNode.tag?.node?.superType === NODE_SUPERTYPE.ACTION && !ie.targetNode.tag.node.excluded)).forEach(ie => {
                    ie.tag.excluded = false
                    ie.sourceNode.tag.includeNode()
                })
            }else if (e.targetNode === node) {
                if (isRoleSystem(e.sourceNode.tag?.node?.superType) && this.filterData.includeNode(e.sourceNode.tag?.node) ) {
                    e.tag.excluded = false
                    // include roles and systems that perform action
                    e.sourceNode.tag.includeNode()

                    if (e.sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE) {
                        graph.edgesAt(e.sourceNode, AdjacencyTypes.INCOMING).filter(ie => ie.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.SYSTEM).forEach(ie => {
                            ie.tag.excluded = false
                            ie.sourceNode.tag.includeNode()
                        })
                    }
                }else if (e.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.INFORMATION && !e.sourceNode.tag.node.excluded) {
                    // previous information node is included so also include edge
                    e.tag.excluded = false
                }
            }
        })
    }

    // includes an action and everything downstream of it
    private populateForwardEdgesForActionNode(node:INode, graph:IGraph) {
        if (this.tracedForward.has(node)) return
        this.tracedForward.add(node)

        this.processActionNode(node, graph)
        graph.edgesAt(node, AdjacencyTypes.OUTGOING).filter(e => e.targetNode.tag?.node?.superType === NODE_SUPERTYPE.INFORMATION).forEach(e => {
            e.tag.excluded = node.tag.excluded
            graph.edgesAt(e.targetNode, AdjacencyTypes.OUTGOING).filter(ie => ie.targetNode.tag?.node?.superType === NODE_SUPERTYPE.ACTION || ie.targetNode.tag?.node?.superType === NODE_SUPERTYPE.OUTCOMES).forEach(ie => {
                ie.tag.excluded = node.tag.excluded
                this.populateForwardEdgesForActionNode(ie.targetNode, graph)
            })
        })
    }

    // includes an action and everything upstream of it
    private populateBackwardEdgesForActionNode(node:INode, graph:IGraph) {
        if (this.tracedBack.has(node)) return
        this.tracedBack.add(node)

        this.processActionNode(node, graph)
        graph.edgesAt(node, AdjacencyTypes.INCOMING).filter(e => e.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.INFORMATION).forEach(e => {
            graph.edgesAt(e.sourceNode, AdjacencyTypes.INCOMING).filter(ie => ie.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.ACTION).forEach(ie => {
                this.populateBackwardEdgesForActionNode(ie.sourceNode, graph)
            })
        })
    }
}